/**
 * Scrolling helpers shared by the onboarding wizard. Form steps scroll inside
 * a dedicated container (see `ScrollAreaContext`) rather than the document, so
 * every helper accepts an optional container and falls back to the window.
 */

/** Matches any field currently rendering a validation error. */
export const INVALID_FIELD_SELECTOR = '[data-error="true"],[aria-invalid="true"]';

/** Space kept above a field when scrolling it into view, in pixels. */
const SCROLL_MARGIN = 24;

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]),select,textarea,button:not([disabled])';

/**
 * Run `callback` once the browser has painted the pending React update, so
 * measurements reflect freshly rendered error states.
 */
export const afterPaint = (callback: () => void): void => {
  requestAnimationFrame(() => requestAnimationFrame(callback));
};

export const scrollAreaToTop = (
  container: HTMLElement | null,
  behavior: ScrollBehavior = 'smooth',
): void => {
  if (container) {
    container.scrollTo({ top: 0, left: 0, behavior });
    return;
  }
  window.scrollTo({ top: 0, left: 0, behavior });
};

export const scrollIntoScrollArea = (
  container: HTMLElement | null,
  element: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
): void => {
  if (!container) {
    element.scrollIntoView({ behavior, block: 'center' });
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const top =
    container.scrollTop + elementRect.top - containerRect.top - SCROLL_MARGIN;

  container.scrollTo({ top: Math.max(top, 0), behavior });
};

/**
 * Focus the field itself or its first text-entry descendant. Scrolling is
 * suppressed so the smooth scroll already in flight isn't cut short.
 */
export const focusInvalidField = (element: HTMLElement): void => {
  const target = element.matches(FOCUSABLE_SELECTOR)
    ? element
    : element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      (element.hasAttribute('tabindex') ? element : null);

  target?.focus({ preventScroll: true });
};

/**
 * Bring the first field rendering a validation error into view and focus it.
 * Returns `true` when such a field was found.
 */
export const scrollToFirstInvalidField = (
  container: HTMLElement | null,
  behavior: ScrollBehavior = 'smooth',
): boolean => {
  const root: ParentNode = container ?? document;
  const field = root.querySelector<HTMLElement>(INVALID_FIELD_SELECTOR);
  if (!field) return false;

  scrollIntoScrollArea(container, field, behavior);
  focusInvalidField(field);
  return true;
};
